/**
 * "Your avatars" in the headset, operated by controller ray (or hand pinch):
 * a paged grid of the avatars you own — pick one to change into it on the
 * spot (services/avatarSwitch) — and "Get a new avatar", which explains that
 * building one happens on a computer or phone (the create flow is a 2D page:
 * checkout, Turnstile and uploads don't work inside an immersive session).
 * Replaces the VR user menu while shown (like the world-hop panel).
 */
import React, { Suspense, useState } from 'react';
import { Text, useTexture } from '@react-three/drei';
import { SRGBColorSpace } from 'three';
import { vrColors as colors, noRaycast, Surface } from '../VR/vrUi';
import { FallbackOnError } from '../three/FallbackOnError';
import { useWorldStore } from '../../stores/worldStore';
import { avatarPicture, switchAvatar, useAvatarSwitch } from '../../services/avatarSwitch';
import type { Avatar } from '../../services/avatarService';

const W = .8;
const cut = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function Button({ label, x, y, w = .17, h = .056, primary = false, disabled = false, name, onPress }: {
  label: string; x: number; y: number; w?: number; h?: number; primary?: boolean; disabled?: boolean; name?: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : primary ? (hover ? '#2ee6d6' : colors.teal) : hover ? colors.hover : colors.card;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    <Surface width={w} height={h} color={bg} r={.012} />
    <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={.018} maxWidth={w - .02}
      color={disabled ? colors.muted : primary ? '#04211e' : colors.text}>{label}</Text>
  </group>;
}

function PicTex({ url, s }: { url: string; s: number }) {
  const tex = useTexture(url);
  tex.colorSpace = SRGBColorSpace;
  return <mesh raycast={noRaycast}><planeGeometry args={[s, s]} /><meshBasicMaterial map={tex} toneMapped={false} /></mesh>;
}
/** Avatar picture quad; a plain tile with a figure glyph if none or it fails. */
function Pic({ url, s }: { url: string | null; s: number }) {
  const plain = <group>
    <mesh raycast={noRaycast}><planeGeometry args={[s, s]} /><meshBasicMaterial color="#163246" toneMapped={false} /></mesh>
    <mesh raycast={noRaycast} position={[0, s * .12, .001]}><circleGeometry args={[s * .14, 24]} /><meshBasicMaterial color="#2c5a72" toneMapped={false} /></mesh>
    <mesh raycast={noRaycast} position={[0, -s * .22, .001]}><circleGeometry args={[s * .26, 32, 0, Math.PI]} /><meshBasicMaterial color="#2c5a72" toneMapped={false} /></mesh>
  </group>;
  if (!url) return plain;
  return <FallbackOnError key={url} what="avatar picture" fallback={plain}>
    <Suspense fallback={plain}><PicTex url={url} s={s} /></Suspense>
  </FallbackOnError>;
}

const TILE_W = .235, TILE_H = .25, PIC = .17;

function Tile({ x, y, label, meta, pic, on, busy, disabled, isNew, name, onPress }: {
  x: number; y: number; label: string; meta: string; pic?: string | null; on?: boolean; busy?: boolean; disabled?: boolean; isNew?: boolean; name: string; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const bg = on ? '#124940' : hover && !disabled ? colors.hover : colors.card;
  return <group position={[x, y, .006]} name={name}
    onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
    onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
    {(on || (hover && !disabled)) && <group position={[0, 0, -.001]}><Surface width={TILE_W + .008} height={TILE_H + .008} color={colors.teal} r={.016} /></group>}
    <Surface width={TILE_W} height={TILE_H} color={bg} r={.014} />
    <group position={[0, .03, .003]}>
      {isNew
        ? <group>
          <mesh raycast={noRaycast}><planeGeometry args={[PIC, PIC]} /><meshBasicMaterial color="#0f3a3a" toneMapped={false} /></mesh>
          <Text raycast={noRaycast} position={[0, .004, .002]} fontSize={.09} color={colors.teal}>+</Text>
        </group>
        : <Pic url={pic ?? null} s={PIC} />}
      {busy && <group position={[0, 0, .002]}>
        <mesh raycast={noRaycast}><planeGeometry args={[PIC, PIC]} /><meshBasicMaterial color="#02080e" transparent opacity={.6} toneMapped={false} /></mesh>
        <Text raycast={noRaycast} position={[0, 0, .002]} fontSize={.02} color={colors.teal}>Loading…</Text>
      </group>}
      {on && <group position={[-PIC / 2 + .045, PIC / 2 - .018, .003]}>
        <Surface width={.08} height={.026} color={colors.teal} r={.012} />
        <Text raycast={noRaycast} position={[0, 0, .002]} fontSize={.012} color="#04211e">WEARING</Text>
      </group>}
    </group>
    <Text raycast={noRaycast} position={[0, -.077, .003]} fontSize={.017} color={colors.text} maxWidth={TILE_W - .02}>{cut(label, 20)}</Text>
    <Text raycast={noRaycast} position={[0, -.103, .003]} fontSize={.012} color={on ? '#71e2cb' : colors.muted} maxWidth={TILE_W - .02}>{meta}</Text>
  </group>;
}

const COLS = 3, ROWS = 2, PAGE = COLS * ROWS;

/** Which avatar surface (if any) replaces the VR menu. */
export function useVRAvatarSwitch() {
  const open = useAvatarSwitch((s) => s.pickerOpen);
  const toast = useAvatarSwitch((s) => s.toast);
  return { open, toast, close: () => useAvatarSwitch.getState().setPickerOpen(false), openPicker: () => useAvatarSwitch.getState().setPickerOpen(true) };
}

export function VRAvatarSwitchPanel({ onClose }: { onClose: () => void }) {
  const avatars = useAvatarSwitch((s) => s.avatars);
  const error = useAvatarSwitch((s) => s.error);
  const switchingId = useAvatarSwitch((s) => s.switchingId);
  const toast = useAvatarSwitch((s) => s.toast);
  const activeId = useWorldStore((s) => s.activeAvatarId);
  const activeUrl = useWorldStore((s) => s.avatarUrl);
  const [page, setPage] = useState(0);
  const [howTo, setHowTo] = useState(false);

  type Item = { kind: 'new' } | { kind: 'avatar'; a: Avatar };
  const items: Item[] = [{ kind: 'new' }, ...(avatars || []).map((a) => ({ kind: 'avatar' as const, a }))];
  const pages = Math.max(1, Math.ceil(items.length / PAGE));
  const shown = items.slice(page * PAGE, page * PAGE + PAGE);
  const worn = (a: Avatar) => (activeId ? a.id === activeId : a.model_url === activeUrl);
  const switching = avatars?.find((a) => a.id === switchingId);
  // The panel grows with the rows in use (one row of tiles, or two).
  const rows = howTo || pages > 1 ? ROWS : Math.max(1, Math.ceil(shown.length / COLS));
  const footY = .095 - (rows - 1) * (TILE_H + .02) - TILE_H / 2 - .045;
  const top = .3, bottom = howTo ? -.39 : footY - .04, H = top - bottom;
  const status = switching ? `Changing into ${cut(switching.name, 26)}…` : toast;

  return <group onClick={(e) => e.stopPropagation()} name="vr-avatar-panel">
    <group position={[0, (top + bottom) / 2, 0]}>
      <Surface width={W + .008} height={H + .008} color={colors.border} z={-.003} />
      <Surface width={W} height={H} color={colors.panel} />
    </group>
    {howTo && <Button label="‹ Back" x={-.32} y={.265} w={.12} name="vr-avatar-back" onPress={() => setHowTo(false)} />}
    <Text raycast={noRaycast} position={[howTo ? -.245 : -.37, .265, .006]} anchorX="left" fontSize={.024} color={colors.text}>
      {howTo ? 'Get a new avatar' : 'Your avatars'}
    </Text>
    <Button label="Close" x={.32} y={.265} w={.12} name="vr-avatar-close" onPress={onClose} />

    {howTo ? <>
      <Text raycast={noRaycast} position={[-.36, .17, .006]} anchorX="left" anchorY="top" fontSize={.021} color={colors.text} maxWidth={.72} lineHeight={1.3}>
        Finish creating your new avatar on a computer or phone.
      </Text>
      <Text raycast={noRaycast} position={[-.36, .085, .006]} anchorX="left" anchorY="top" fontSize={.017} color={colors.muted} maxWidth={.72} lineHeight={1.4}>
        {'Go to gathrxr.com/avatars/new and sign in with this account. Pick a free robot or build your own there.\n\nWhen it\'s ready it appears in this list, so you can change into it right here without leaving the world.'}
      </Text>
      <group position={[0, -.22, .006]}>
        <Surface width={.5} height={.07} color="#0a141f" r={.014} />
        <Text raycast={noRaycast} position={[0, 0, .003]} fontSize={.024} color={colors.teal}>gathrxr.com/avatars/new</Text>
      </group>
      <Button label="Back to my avatars" x={0} y={-.32} w={.3} h={.05} name="vr-avatar-howto-back" onPress={() => setHowTo(false)} />
    </> : <>
      {avatars === null
        ? <Text raycast={noRaycast} position={[0, 0, .006]} fontSize={.02} color={colors.muted}>Loading your avatars…</Text>
        : shown.map((it, i) => {
          const x = (i % COLS - 1) * (TILE_W + .025);
          const y = .095 - Math.floor(i / COLS) * (TILE_H + .02);
          if (it.kind === 'new') {
            return <Tile key="new" x={x} y={y} isNew label="Get a new avatar" meta="Free robot or build one" name="vr-avatar-new"
              onPress={() => setHowTo(true)} />;
          }
          const on = worn(it.a);
          const busy = switchingId === it.a.id;
          return <Tile key={it.a.id} x={x} y={y} label={it.a.name} pic={avatarPicture(it.a)} on={on} busy={busy}
            disabled={on || !!switchingId} meta={on ? 'You, right now' : busy ? 'Loading…' : 'Change into'}
            name={`vr-avatar-${it.a.id}`} onPress={() => void switchAvatar(it.a)} />;
        })}
      {error && <Text raycast={noRaycast} position={[0, footY, .006]} fontSize={.015} color="#ffb1ad" maxWidth={.74}>{error}</Text>}
      {pages > 1 && <>
        <Button label="‹ Prev" x={-.3} y={footY} w={.14} h={.044} disabled={page === 0} onPress={() => setPage((n) => Math.max(0, n - 1))} />
        <Button label="Next ›" x={.3} y={footY} w={.14} h={.044} disabled={page >= pages - 1} name="vr-avatar-next" onPress={() => setPage((n) => Math.min(pages - 1, n + 1))} />
      </>}
      {status && !error && <Text raycast={noRaycast} position={[0, footY, .006]} fontSize={.016} color={colors.teal} maxWidth={.4}>{status}</Text>}
      {!status && !error && pages > 1 && <Text raycast={noRaycast} position={[0, footY, .006]} fontSize={.016} color={colors.muted}>{`${page + 1} / ${pages}`}</Text>}
    </>}
  </group>;
}
